import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, SmartphoneIcon, PhoneIcon, BellIcon, MailIcon, GlobeIcon, MessageSquareIcon, MapPinIcon, BarChart3Icon, UsersIcon, ShieldAlertIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  traditional: 'Diffusion à une liste',
  traditionalEn: 'Broadcast to a list',
  bciti: 'Personnes vérifiées ciblées par localisation',
  bcitiEn: 'Verified people targeted by location'
}, {
  traditional: 'Un canal à la fois',
  traditionalEn: 'One channel at a time',
  bciti: 'SMS · Voix · Push · Courriel · Web · Réseaux sociaux coordonnés',
  bcitiEn: 'SMS · Voice · Push · Email · Web · Social coordinated'
}, {
  traditional: 'Envoyé',
  traditionalEn: 'Sent',
  bciti: 'Livré et mesurable',
  bcitiEn: 'Delivered and measurable'
}, {
  traditional: 'Outil d\u2019urgence isolé',
  traditionalEn: 'Standalone emergency tool',
  bciti: 'Mêmes rails pour les communications d\u2019urgence et quotidiennes',
  bcitiEn: 'Same rails for emergency and daily communications'
}, {
  traditional: 'Mises à jour manuelles des audiences',
  traditionalEn: 'Manual audience updates',
  bciti: 'Mises à jour d\u2019audiences propulsées par l\u2019identité',
  bcitiEn: 'Identity-powered audience updates'
}, {
  traditional: 'Contacts anonymes',
  traditionalEn: 'Anonymous contacts',
  bciti: 'Identités vérifiées',
  bcitiEn: 'Verified identities'
}];
const WORKFLOW = [{
  fr: 'Urgence',
  en: 'Emergency'
}, {
  fr: 'Avis de service',
  en: 'Service Notice'
}, {
  fr: 'Rappel',
  en: 'Reminder'
}, {
  fr: 'Renouvellement',
  en: 'Renewal'
}, {
  fr: 'Engagement',
  en: 'Engagement'
}];
export function Communications() {
  const {
    lang
  } = useLanguage();
  useEffect(() => {
    track('Page Viewed', {
      page: 'Communications'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-1717">
      <Header data-id="element-1718" />

      <main className="pt-[120px]" data-id="element-1719">
        {/* SECTION 1: HOOK */}
        <section className="pt-24 pb-32 px-6" data-id="element-1720">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1721">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-1722">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-6" data-id="element-1723">
                BCITI Communications
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-1724">
                {lang === 'FR' ? 'Les bonnes personnes. Le bon moment. La preuve.' : 'The right people. The right moment. The proof.'}
              </h1>
              <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-10" data-id="element-1725">
                {lang === 'FR' ? 'Des résidents vérifiés, ciblés par localisation, rejoints sur tous les canaux — y compris la voix automatisée quand ça compte le plus.' : 'Verified residents, targeted by location, reached across every channel — including automated voice when it matters most.'}
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-10" data-id="element-1726">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Communications Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1727">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1728" />
                </Link>
                <a href="#proof" onClick={e => {
                e.preventDefault();
                document.getElementById('proof')?.scrollIntoView({
                  behavior: 'smooth'
                });
                track('Proof CTA Clicked', {
                  location: 'Communications Hero'
                });
              }} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-1729">
                  {lang === 'FR' ? 'Voir en production' : 'See It In Production'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1730" />
                </a>
              </div>

              <div className="flex items-center gap-3 font-sans text-xs font-medium text-[#121212]/50 tracking-wider uppercase" data-id="element-1731">
                <span data-id="element-1732">{lang === 'FR' ? 'Urgence' : 'Emergency'}</span>
                <span className="w-1 h-1 rounded-full bg-[#E4E6EC]" data-id="element-1733" />
                <span data-id="element-1734">
                  {lang === 'FR' ? 'Avis de service' : 'Service Notices'}
                </span>
                <span className="w-1 h-1 rounded-full bg-[#E4E6EC]" data-id="element-1735" />
                <span data-id="element-1736">{lang === 'FR' ? 'Rappels' : 'Reminders'}</span>
                <span className="w-1 h-1 rounded-full bg-[#E4E6EC]" data-id="element-1737" />
                <span data-id="element-1738">{lang === 'FR' ? 'Engagement' : 'Engagement'}</span>
              </div>
            </motion.div>

            {/* HERO VISUAL: Communication Command Center */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex justify-center" data-id="element-1739">
              <div className="w-full max-w-lg border border-[#E4E6EC] bg-[#FAFAFB] p-4" data-id="element-1740">
                <div className="border border-[#E4E6EC] bg-white" data-id="element-1741">
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E6EC]" data-id="element-1742">
                    <div className="flex items-center gap-2" data-id="element-1743">
                      <span className="inline-block w-3 h-3 bg-[#4F46E5]" aria-hidden="true" data-id="element-1744" />
                      <span className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212]/70" data-id="element-1745">
                        {lang === 'FR' ? 'Centre de commande' : 'Command Center'}
                      </span>
                    </div>
                    <span className="font-sans text-[11px] text-[#121212]/40" data-id="element-1746">
                      {lang === 'FR' ? 'Opérations en direct' : 'Live Operations'}
                    </span>
                  </div>

                  {/* Campaign Status */}
                  <div className="px-5 py-6 border-b border-[#E4E6EC]" data-id="element-1747">
                    <div className="flex justify-between items-start mb-6" data-id="element-1748">
                      <div data-id="element-1749">
                        <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-1" data-id="element-1750">
                          {lang === 'FR' ? 'Campagne active' : 'Active Campaign'}
                        </div>
                        <div className="font-serif text-2xl text-[#121212]" data-id="element-1751">
                          {lang === 'FR' ? 'Fermeture de route — Secteur B' : 'Road Closure — Sector B'}
                        </div>
                      </div>
                      <div className="px-3 py-1 bg-[#4F46E5]/10 text-[#4F46E5] font-sans text-xs font-medium border border-[#4F46E5]/20" data-id="element-1752">
                        {lang === 'FR' ? <>En cours d{'\u2019'}envoi</> : 'Delivering'}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1753">
                      <div data-id="element-1754">
                        <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-1" data-id="element-1755">
                          {lang === 'FR' ? <>Portée d{'\u2019'}audience</> : 'Audience Reach'}
                        </div>
                        <div className="font-serif text-xl text-[#121212]" data-id="element-1756">
                          {lang === 'FR' ? <>14{'\u00A0'}250</> : '14,250'}
                        </div>
                      </div>
                      <div data-id="element-1757">
                        <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-1" data-id="element-1758">
                          {lang === 'FR' ? 'Statut de livraison' : 'Delivery Status'}
                        </div>
                        <div className="font-serif text-xl text-[#121212]" data-id="element-1759">
                          {lang === 'FR' ? <>98,4{'\u00A0'}%</> : '98.4%'}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3" data-id="element-1760">
                      <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40" data-id="element-1761">
                        {lang === 'FR' ? 'Canaux utilisés' : 'Channels Used'}
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-1762">
                        {(lang === 'FR' ? ['SMS', 'Voix automatisée', 'Push', 'Courriel', 'Bannière web', 'Réseaux sociaux'] : ['SMS', 'Automated Voice', 'Push', 'Email', 'Website Banner', 'Social Media']).map(channel => <span key={channel} className="px-2 py-1 border border-[#E4E6EC] text-[11px] font-sans text-[#121212]/70 bg-[#FAFAFB]" data-id="element-1763">
                            {channel}
                          </span>)}
                      </div>
                    </div>
                  </div>

                  {/* Map Targeting & Analytics */}
                  <div className="px-5 py-4 flex items-start gap-4" data-id="element-1764">
                    <div className="flex-1" data-id="element-1765">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1766">
                        <MapPinIcon className="w-4 h-4 text-[#121212]/40" data-id="element-1767" />
                        <span className="font-sans text-sm font-medium text-[#121212]" data-id="element-1768">
                          {lang === 'FR' ? 'Portée géographique' : 'Geographic Reach'}
                        </span>
                      </div>
                      <div className="font-sans text-xs text-[#121212]/55 ml-6" data-id="element-1769">
                        {lang === 'FR' ? 'Ciblage cartographique actif' : 'Map-based targeting active'}
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-1770">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1771">
                        <UsersIcon className="w-4 h-4 text-[#121212]/40" data-id="element-1772" />
                        <span className="font-sans text-sm font-medium text-[#121212]" data-id="element-1773">
                          {lang === 'FR' ? 'Croissance des abonnés' : 'Subscriber Growth'}
                        </span>
                      </div>
                      <div className="font-sans text-xs text-[#121212]/55 ml-6" data-id="element-1774">
                        {lang === 'FR' ? <>+124 alertes d{'\u2019'}urgence</> : '+124 Emergency Alerts'}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="font-sans text-[11px] text-[#121212]/40 text-center mt-4" data-id="element-1775">
                  {lang === 'FR' ? <>
                      C{'\u2019'}est une infrastructure opérationnelle, pas une
                      application de messagerie.
                    </> : 'This is operational infrastructure, not a messaging app.'}
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: CONTRAST */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1776">
          <div className="max-w-4xl mx-auto" data-id="element-1777">
            <div className="text-center mb-20" data-id="element-1778">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6" data-id="element-1779">
                {lang === 'FR' ? 'La notification de masse envoie. Communications rejoint.' : 'Mass notification sends. Communications reaches.'}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-xl text-[#121212]/60 max-w-2xl mx-auto" data-id="element-1780">
                {lang === 'FR' ? <>
                    Le même budget d{'\u2019'}alertes — mais vous savez
                    désormais qui a été rejoint, où, et sur quel canal.
                  </> : 'The same alert budget — but now you know who was reached, where they were reached, and on which channel.'}
              </motion.p>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="bg-white border border-[#E4E6EC] overflow-hidden mb-12" data-id="element-1781">
              <div className="grid grid-cols-2 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1782">
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase border-r border-[#E4E6EC]" data-id="element-1783">
                  {lang === 'FR' ? 'Approche traditionnelle' : 'Traditional Approach'}
                </div>
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase" data-id="element-1784">
                  BCITI Communications
                </div>
              </div>
              {COMPARISON_ROWS.map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-[#E4E6EC] last:border-0 hover:bg-[#FAFAFB]/50 transition-colors" data-id="element-1785">
                  <div className="p-6 font-serif text-lg text-[#121212]/50 border-r border-[#E4E6EC]" data-id="element-1786">
                    {lang === 'FR' ? row.traditional : row.traditionalEn}
                  </div>
                  <div className="p-6 font-serif text-lg text-[#121212]" data-id="element-1787">
                    {lang === 'FR' ? row.bciti : row.bcitiEn}
                  </div>
                </div>)}
            </motion.div>
          </div>
        </section>

        {/* SECTION 2b: IDENTITY CREATES CONTEXT — text-only transition */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-1788">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1789">
            <motion.span {...reveal} className="block font-sans text-xs font-semibold tracking-[0.25em] text-indigo uppercase mb-8" data-id="element-1790">
              {lang === 'FR' ? <>L{'\u2019'}identité crée le contexte</> : 'Identity Creates Context'}
            </motion.span>

            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="font-serif text-2xl text-[#121212] max-w-2xl mx-auto leading-snug" data-id="element-1791">
              {lang === 'FR' ? <>
                  Tout le monde diffuse. La nôtre sait qui rejoindre, où, et sur
                  quel canal — parce qu{'\u2019'}elle connaît l{'\u2019'}
                  identité vérifiée derrière chaque abonnement.
                </> : 'Everyone broadcasts. Ours knows who to reach, where, and on which channel — because it knows the verified identity behind every subscription.'}
            </motion.p>
          </div>
        </section>

        {/* SECTION 2c: SENDING IS AN ACTIVITY */}
        <section className="py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1792">
          <div className="max-w-4xl mx-auto" data-id="element-1793">
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center max-w-2xl mx-auto" data-id="element-1794">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" aria-hidden="true" data-id="element-1795" />
              <p className="font-serif text-3xl text-[#121212]" data-id="element-1796">
                {lang === 'FR' ? 'Envoyer est une activité.' : 'Sending is an activity.'}
              </p>
              <p className="font-serif text-3xl text-[#121212]/50 mt-2" data-id="element-1797">
                {lang === 'FR' ? 'La portée est un actif.' : 'Reach is an asset.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* DISCREET BAND: ONE INFRASTRUCTURE, EVERY COMMUNITY */}
        <section className="py-10 px-6 border-t border-[#E4E6EC]" data-id="element-1798">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1799">
            <motion.p {...reveal} className="font-serif text-lg text-[#121212] leading-snug mb-6" data-id="element-1800">
              {lang === 'FR' ? 'La même infrastructure de communication, chaque communauté.' : 'The same communication infrastructure, every community.'}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="flex flex-wrap justify-center gap-3" data-id="element-1801">
              {(lang === 'FR' ? ['Villes', 'Universités', 'Aéroports', 'Télécom', 'Santé'] : ['Cities', 'Universities', 'Airports', 'Telecom', 'Health']).map(item => <span key={item} className="px-4 py-1.5 border border-[#E4E6EC] bg-white font-sans text-sm text-[#121212] hover:border-indigo hover:text-indigo transition-colors" data-id="element-1802">
                  {item}
                </span>)}
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: THE COMPOUNDING ASSET */}
        <section id="emergency" className="py-32 px-6 border-t border-[#E4E6EC] scroll-mt-24" data-id="element-1803">
          <div className="max-w-5xl mx-auto" data-id="element-1804">
            <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-1805">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8 leading-tight" data-id="element-1806">
                {lang === 'FR' ? <>Posez les rails pour l{'\u2019'}urgence. Le reste suit.</> : 'Lay the rails for emergency. Everything else follows.'}
              </motion.h2>
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="space-y-6 font-sans text-xl text-[#121212]/70 leading-relaxed" data-id="element-1807">
                {lang === 'FR' ? <>
                    <p data-id="element-1808">
                      Les organisations investissent déjà dans les
                      communications d{'\u2019'}urgence.
                    </p>
                    <p data-id="element-1809">
                      Une fois les personnes joignables sur des canaux vérifiés,
                      chaque communication future passe par la même
                      infrastructure.
                    </p>
                    <p data-id="element-1810">
                      Avis de service. Rappels. Renouvellements. Engagement
                      communautaire.
                    </p>
                    <p data-id="element-1811">
                      Aucune liste à reconstruire. Aucune base de données à
                      recréer. La portée se cumule.
                    </p>
                  </> : <>
                    <p data-id="element-1812">
                      Organizations already invest in emergency communications.
                    </p>
                    <p data-id="element-1813">
                      Once people are reachable on verified channels, every
                      future communication runs on the same infrastructure.
                    </p>
                    <p data-id="element-1814">
                      Service notices. Reminders. Renewals. Community
                      engagement.
                    </p>
                    <p data-id="element-1815">
                      No lists to rebuild. No databases to recreate. Reach
                      compounds.
                    </p>
                  </>}
              </motion.div>
            </div>

            {/* SINGLE WORKFLOW */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-col md:flex-row items-center justify-center gap-4 mb-16" data-id="element-1816">
              {WORKFLOW.map((node, i, arr) => <Fragment key={node.en} data-id="element-1817">
                  <div className={`px-6 py-4 border ${i === 0 ? 'border-[#4F46E5] bg-[#4F46E5]/5 text-[#4F46E5] font-medium' : 'border-[#E4E6EC] bg-white text-[#121212]'} font-sans text-sm`} data-id="element-1818">
                    {lang === 'FR' ? node.fr : node.en}
                  </div>
                  {i < arr.length - 1 && <ArrowRightIcon className="w-5 h-5 text-[#E4E6EC] rotate-90 md:rotate-0" data-id="element-1819" />}
                </Fragment>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center mb-20" data-id="element-1820">
              <p className="font-serif text-2xl text-[#121212]" data-id="element-1821">
                {lang === 'FR' ? <>Atterrissez sur l{'\u2019'}urgence.</> : 'Land on emergency.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]/50 mt-1" data-id="element-1822">
                {lang === 'FR' ? 'Étendez-vous sur tout le reste.' : 'Expand on everything else.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4: PROOF */}
        <section id="proof" className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB] scroll-mt-24" data-id="element-1823">
          <div className="max-w-5xl mx-auto" data-id="element-1824">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-1825">
              {lang === 'FR' ? <>En production aujourd{'\u2019'}hui.</> : 'In production today.'}
            </motion.h2>

            {/* Three Executive Proof Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-8" data-id="element-1826">
              {(lang === 'FR' ? [{
              stat: '400K+',
              label: 'Abonnés aux alertes d\u2019urgence',
              desc: 'Joignables en quelques minutes'
            }, {
              stat: '20',
              label: 'Municipalités en production',
              desc: 'Communications en production'
            }, {
              stat: 'Depuis 2017',
              label: 'Infrastructure de production',
              desc: 'En activité aujourd\u2019hui'
            }] : [{
              stat: '400K+',
              label: 'Emergency Alert Subscribers',
              desc: 'Reachable in minutes'
            }, {
              stat: '20',
              label: 'Municipalities in Production',
              desc: 'Communications Live'
            }, {
              stat: 'Since 2017',
              label: 'Production Infrastructure',
              desc: 'Operating Today'
            }]).map(card => <div key={card.label} className="bg-white p-10" data-id="element-1827">
                  <div className="font-serif text-4xl md:text-5xl text-[#121212] mb-3" data-id="element-1828">
                    {card.stat}
                  </div>
                  <div className="font-sans text-sm font-semibold tracking-wide text-[#4F46E5] uppercase mb-4" data-id="element-1829">
                    {card.label}
                  </div>
                  <p className="font-sans text-sm text-[#121212]/60 leading-relaxed" data-id="element-1830">
                    {card.desc}
                  </p>
                </div>)}
            </div>

            <p className="font-sans text-lg text-[#121212]/70 text-center max-w-3xl mx-auto mb-20" data-id="element-1831">
              {lang === 'FR' ? <>
                  Plus de 300{'\u00A0'}000 personnes inscrites aux alertes d
                  {'\u2019'}urgence sur un réseau de plus de 400{'\u00A0'}000
                  identités vérifiées et 20 municipalités en production.
                </> : 'More than 300,000 people registered for emergency alerts across a network of 400K+ verified identities and 20 municipalities in production.'}
            </p>

            {/* COMMUNICATIONS COMMAND CENTER */}
            <div className="bg-white border border-[#E4E6EC] p-2 md:p-4 shadow-sm mb-12" data-id="element-1832">
              <div className="border border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1833">
                <div className="border-b border-[#E4E6EC] px-6 py-4 flex items-center justify-between bg-white" data-id="element-1834">
                  <div className="font-sans text-sm font-semibold tracking-widest text-[#121212]/40 uppercase" data-id="element-1835">
                    {lang === 'FR' ? 'Centre de contrôle des communications' : 'Mission Control for Communications'}
                  </div>
                  <div className="flex gap-2" data-id="element-1836">
                    <span className="w-2 h-2 rounded-full bg-[#E4E6EC]" data-id="element-1837" />
                    <span className="w-2 h-2 rounded-full bg-[#E4E6EC]" data-id="element-1838" />
                    <span className="w-2 h-2 rounded-full bg-[#E4E6EC]" data-id="element-1839" />
                  </div>
                </div>
                <div className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1840">
                  <div className="space-y-8" data-id="element-1841">
                    <div data-id="element-1842">
                      <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-4" data-id="element-1843">
                        {lang === 'FR' ? 'Performance' : 'Performance'}
                      </div>
                      <ul className="space-y-3 font-sans text-sm text-[#121212]" data-id="element-1844">
                        <li className="flex items-center gap-2" data-id="element-1845">
                          <UsersIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1846" />
                          {lang === 'FR' ? 'Croissance des abonnés' : 'Subscriber Growth'}
                        </li>
                        <li className="flex items-center gap-2" data-id="element-1847">
                          <BarChart3Icon className="w-4 h-4 text-[#4F46E5]" data-id="element-1848" />
                          {lang === 'FR' ? 'Portée des campagnes' : 'Campaign Reach'}
                        </li>
                        <li className="flex items-center gap-2" data-id="element-1849">
                          <MapPinIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1850" />
                          {lang === 'FR' ? 'Portée géographique' : 'Geographic Reach'}
                        </li>
                      </ul>
                    </div>
                    <div data-id="element-1851">
                      <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-4" data-id="element-1852">
                        {lang === 'FR' ? 'Livraisons par canal' : 'Deliveries by Channel'}
                      </div>
                      <ul className="space-y-3 font-sans text-sm text-[#121212]" data-id="element-1853">
                        <li className="flex items-center gap-2" data-id="element-1854">
                          <SmartphoneIcon className="w-4 h-4 text-[#121212]/40" data-id="element-1855" />
                          {lang === 'FR' ? 'Livraison SMS' : 'SMS Delivery'}
                        </li>
                        <li className="flex items-center gap-2" data-id="element-1856">
                          <PhoneIcon className="w-4 h-4 text-[#121212]/40" data-id="element-1857" />
                          {lang === 'FR' ? 'Livraison vocale' : 'Voice Delivery'}
                        </li>
                        <li className="flex items-center gap-2" data-id="element-1858">
                          <BellIcon className="w-4 h-4 text-[#121212]/40" data-id="element-1859" />
                          {lang === 'FR' ? 'Livraison push' : 'Push Delivery'}
                        </li>
                        <li className="flex items-center gap-2" data-id="element-1860">
                          <MailIcon className="w-4 h-4 text-[#121212]/40" data-id="element-1861" />
                          {lang === 'FR' ? 'Livraison courriel' : 'Email Delivery'}
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="md:col-span-2 space-y-8" data-id="element-1862">
                    <div data-id="element-1863">
                      <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-4" data-id="element-1864">
                        {lang === 'FR' ? 'Abonnements par sujet' : 'Topic Subscriptions'}
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-1865">
                        {(lang === 'FR' ? ['Communications d\u2019urgence', 'Communications de service', 'Communications aux employés', 'Communications aux entreprises'] : ['Emergency Communications', 'Service Communications', 'Employee Communications', 'Business Communications']).map(topic => <div key={topic} className="p-4 border border-[#E4E6EC] bg-white font-sans text-sm text-[#121212]" data-id="element-1866">
                            {topic}
                          </div>)}
                      </div>
                    </div>
                    <div className="p-8 border border-[#E4E6EC] bg-white flex flex-col items-center justify-center min-h-[200px]" data-id="element-1867">
                      <BarChart3Icon className="w-8 h-8 text-[#E4E6EC] mb-4" data-id="element-1868" />
                      <div className="font-sans text-sm text-[#121212]/40" data-id="element-1869">
                        {lang === 'FR' ? <>Analytique de segmentation d{'\u2019'}audience</> : 'Audience Segmentation Analytics'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center" data-id="element-1870">
              <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-base font-medium text-[#4F46E5] hover:gap-3 transition-all" data-id="element-1871">
                {lang === 'FR' ? 'Découvrir Communications en production' : 'Explore Communications in Production'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-1872" />
              </Link>
            </div>

            {/* Confidence-peak CTA — glued directly to the production proof */}
            <div className="text-center mt-16" data-id="element-1873">
              <p className="font-serif text-2xl text-[#121212] mb-6" data-id="element-1874">
                {lang === 'FR' ? <>
                    C{'\u2019'}est en production aujourd{'\u2019'}hui.
                  </> : 'This is running in production today.'}
              </p>
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Communications Production Proof'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1875">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1876" />
              </Link>
            </div>
          </div>
        </section>

        {/* SECTION 5: WHAT'S INSIDE */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-1877">
          <div className="max-w-6xl mx-auto" data-id="element-1878">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-20" data-id="element-1879">
              {lang === 'FR' ? <>Ce qu{'\u2019'}il contient.</> : "What's inside."}
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16" data-id="element-1880">
              {/* CHANNELS */}
              <div className="border border-[#E4E6EC] p-8" data-id="element-1881">
                <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6" data-id="element-1882">
                  {lang === 'FR' ? 'Canaux' : 'Channels'}
                </div>
                <ul className="space-y-4 font-sans text-lg text-[#121212]/80" data-id="element-1883">
                  {(lang === 'FR' ? ['SMS', 'Voix automatisée', 'Notifications push', 'Courriel', 'Bannière web', 'Réseaux sociaux'] : ['SMS', 'Automated Voice', 'Push Notifications', 'Email', 'Website Banner', 'Social Media']).map(item => <li key={item} data-id="element-1884">{item}</li>)}
                </ul>
              </div>

              {/* TARGETING */}
              <div className="border border-[#E4E6EC] p-8" data-id="element-1885">
                <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6" data-id="element-1886">
                  {lang === 'FR' ? 'Ciblage' : 'Targeting'}
                </div>
                <ul className="space-y-4 font-sans text-lg text-[#121212]/80" data-id="element-1887">
                  {(lang === 'FR' ? ['Géorepérage', 'Zones prédéfinies', 'Adresses', 'Rues', 'Codes postaux', 'Ciblage cartographique'] : ['Geofencing', 'Predefined Zones', 'Addresses', 'Streets', 'Postal Codes', 'Map-Based Targeting']).map(item => <li key={item} data-id="element-1888">{item}</li>)}
                </ul>
              </div>

              {/* AUDIENCES */}
              <div className="border border-[#E4E6EC] p-8" data-id="element-1889">
                <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6" data-id="element-1890">
                  {lang === 'FR' ? 'Audiences' : 'Audiences'}
                </div>
                <ul className="space-y-4 font-sans text-lg text-[#121212]/80" data-id="element-1891">
                  {(lang === 'FR' ? ['Résidents', 'Non-résidents', 'Employés', 'Entreprises', 'Abonnés', 'Visiteurs'] : ['Residents', 'Non-Residents', 'Employees', 'Businesses', 'Subscribers', 'Visitors']).map(item => <li key={item} data-id="element-1892">{item}</li>)}
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" data-id="element-1893">
              {/* Highlight Card: AUTOMATED VOICE */}
              <div className="border border-[#E4E6EC] bg-[#FAFAFB] p-10" data-id="element-1894">
                <div className="flex items-center gap-3 mb-4" data-id="element-1895">
                  <PhoneIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-1896" />
                  <h3 className="font-serif text-2xl text-[#121212]" data-id="element-1897">
                    {lang === 'FR' ? 'Voix automatisée' : 'Automated Voice'}
                  </h3>
                </div>
                <p className="font-sans text-lg text-[#121212]/60 mb-8" data-id="element-1898">
                  {lang === 'FR' ? 'Quand chaque minute compte.' : 'When minutes matter.'}
                </p>
                <div className="font-sans text-sm font-semibold tracking-widest text-[#121212]/40 uppercase mb-4" data-id="element-1899">
                  {lang === 'FR' ? <>Idéal pour{'\u00A0'}:</> : 'Ideal for:'}
                </div>
                <ul className="space-y-3 font-sans text-base text-[#121212]/80" data-id="element-1900">
                  {(lang === 'FR' ? ['Notifications d\u2019urgence', 'Évacuations', 'Interruptions de service', 'Communications critiques'] : ['Emergency Notifications', 'Evacuations', 'Service Interruptions', 'Critical Communications']).map(item => <li key={item} className="flex items-center gap-3" data-id="element-1901">
                      <ShieldAlertIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1902" />
                      {item}
                    </li>)}
                </ul>
              </div>

              {/* TOPICS & SUBSCRIPTIONS */}
              <div className="border border-[#E4E6EC] p-10" data-id="element-1903">
                <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6" data-id="element-1904">
                  {lang === 'FR' ? 'Sujets et abonnements' : 'Topics & Subscriptions'}
                </div>
                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1905">
                  {(lang === 'FR' ? ['Alertes d\u2019urgence', 'Fermetures de routes', 'Collecte', 'Événements', 'Nouvelles de la communauté', 'Services publics', 'Mises à jour de service'] : ['Emergency Alerts', 'Road Closures', 'Collect', 'Events', 'Community News', 'Utilities', 'Service Updates']).map(topic => <div key={topic} className="font-sans text-base text-[#121212]/80" data-id="element-1906">
                      {topic}
                    </div>)}
                </div>
                <div className="pt-6 border-t border-[#E4E6EC]" data-id="element-1907">
                  <p className="font-serif text-xl text-[#121212]" data-id="element-1908">
                    {lang === 'FR' ? <>Les utilisateurs s{'\u2019'}abonnent par sujet.</> : 'Users subscribe by topic.'}
                  </p>
                  <p className="font-serif text-xl text-[#121212]/60" data-id="element-1909">
                    {lang === 'FR' ? 'Les organisations communiquent avec précision.' : 'Organizations communicate with precision.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" data-id="element-1910">
              {/* COMPOSITION */}
              <div className="border border-[#4F46E5]/20 bg-[#4F46E5]/[0.02] p-10 relative" data-id="element-1911">
                <div className="absolute -top-3 left-10 bg-[#4F46E5] text-white font-sans text-[10px] font-bold tracking-widest uppercase px-3 py-1.5" data-id="element-1912">
                  {lang === 'FR' ? 'Propulsé par ATNA' : 'Powered by ATNA'}
                </div>
                <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6 mt-2" data-id="element-1913">
                  {lang === 'FR' ? 'Composition' : 'Composition'}
                </div>
                <ul className="space-y-4 font-sans text-lg text-[#121212]/80" data-id="element-1914">
                  {(lang === 'FR' ? ['Réutiliser un avis existant', 'Créer un nouvel avis', 'Aide à la traduction', 'Aperçu multicanal', 'Recommandations de portée optimale', 'Recommandations de canaux', 'Optimisation des messages'] : ['Reuse Existing Notice', 'Create New Notice', 'Translation Assistance', 'Multichannel Preview', 'Optimal Reach Recommendations', 'Channel Recommendations', 'Message Optimization']).map(item => <li key={item} data-id="element-1915">{item}</li>)}
                </ul>
              </div>

              {/* ANALYTICS */}
              <div className="border border-[#E4E6EC] p-10" data-id="element-1916">
                <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6" data-id="element-1917">
                  {lang === 'FR' ? 'Analytique' : 'Analytics'}
                </div>
                <ul className="space-y-4 font-sans text-lg text-[#121212]/80" data-id="element-1918">
                  {(lang === 'FR' ? ['Abonnés par sujet', 'Messages envoyés', 'Messages livrés', 'Livraison par canal', 'Livraison par segment', 'Portée géographique', 'Indicateurs d\u2019engagement', 'Tendances de croissance'] : ['Subscribers by Topic', 'Messages Sent', 'Messages Delivered', 'Delivery by Channel', 'Delivery by Segment', 'Geographic Reach', 'Engagement Metrics', 'Growth Trends']).map(item => <li key={item} data-id="element-1919">{item}</li>)}
                </ul>
              </div>
            </div>

            <p className="font-sans text-xs text-[#121212]/40 text-center max-w-3xl mx-auto" data-id="element-1920">
              {lang === 'FR' ? <>
                  *«{'\u00A0'}Livré{'\u00A0'}» s{'\u2019'}applique uniquement
                  aux canaux mesurant la livraison, soit SMS, Voix, Push et
                  Courriel. Web et Réseaux sociaux sont rapportés comme canaux
                  de portée.
                </> : '*Delivered applies only to channels supporting delivery measurement, including SMS, Voice, Push and Email. Website and Social Media are reported as reach channels.'}
            </p>
          </div>
        </section>

        {/* SECTION 6: FINAL POSITIONING */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1921">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1922">
            <motion.h2 {...reveal} className="font-serif text-5xl text-[#121212] mb-10 leading-tight" data-id="element-1923">
              {lang === 'FR' ? <>
                  La liste n{'\u2019'}est pas l{'\u2019'}actif.
                </> : 'The list is not the asset.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="space-y-2 font-sans text-xl text-[#121212]/70 mb-12" data-id="element-1924">
              {lang === 'FR' ? <>
                  <p data-id="element-1925">
                    Des personnes vérifiées. Des audiences segmentées. Des
                    abonnements par sujet.
                  </p>
                  <p data-id="element-1926">Joignables en quelques minutes sur six canaux.</p>
                  <p className="font-medium text-[#121212] mt-6" data-id="element-1927">
                    Voilà l{'\u2019'}actif.
                  </p>
                  <p data-id="element-1928">Il croît avec le temps.</p>
                  <p data-id="element-1929">Et devient de plus en plus difficile à remplacer.</p>
                </> : <>
                  <p data-id="element-1930">
                    Verified people. Segmented audiences. Topic subscriptions.
                  </p>
                  <p data-id="element-1931">Reachable in minutes across six channels.</p>
                  <p className="font-medium text-[#121212] mt-6" data-id="element-1932">
                    That is the asset.
                  </p>
                  <p data-id="element-1933">It grows over time.</p>
                  <p data-id="element-1934">And becomes increasingly difficult to replace.</p>
                </>}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="mb-16" data-id="element-1935">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" data-id="element-1936" />
              <p className="font-serif text-2xl text-[#121212]/60" data-id="element-1937">
                {lang === 'FR' ? <>L{'\u2019'}identité propulse les communications.</> : 'Identity powers communications.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]/60" data-id="element-1938">
                {lang === 'FR' ? 'Les communications propulsent l\u2019engagement.' : 'Communications power engagement.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]" data-id="element-1939">
                {lang === 'FR' ? <>
                    L{'\u2019'}engagement propulse l{'\u2019'}intelligence.
                  </> : 'Engagement powers intelligence.'}
              </p>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} data-id="element-1940">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Communications Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1941">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1942" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1943" />
    </div>;
}